import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchGoogleAdsSearchTermsDirect, fetchSearchConsoleQuery } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Search, Calendar, AlertCircle, Target, Globe2,
  ArrowUp, ArrowDown, ArrowUpDown, DollarSign,
} from "lucide-react";

/* ─────────────── period utilities (Performance-style 3-mode selector) ─────────────── */

const MONTH_NAMES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

const PERIOD_PRESETS = [
  { id: "month",  label: "Mes" },
  { id: "months", label: "Rango de meses" },
  { id: "dates",  label: "Rango de fechas" },
];

function buildMonthOptions(n = 24) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const m = String(d.getMonth() + 1).padStart(2, "0");
    out.push({ value: `${d.getFullYear()}-${m}`, label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}` });
  }
  return out;
}

const iso = (d) => d.toISOString().slice(0, 10);

function periodToDates(preset, monthSingle, monthFrom, monthTo, customFrom, customTo) {
  if (preset === "month") {
    if (!monthSingle) return null;
    const [y, m] = monthSingle.split("-").map(Number);
    const today = new Date();
    const from = new Date(y, m - 1, 1);
    const last = new Date(y, m, 0);
    const to = last > today ? today : last;
    return { start_date: iso(from), end_date: iso(to) };
  }
  if (preset === "months") {
    if (!monthFrom || !monthTo) return null;
    const [yF, mF] = monthFrom.split("-").map(Number);
    const [yT, mT] = monthTo.split("-").map(Number);
    const today = new Date();
    const from = new Date(yF, mF - 1, 1);
    const lastTo = new Date(yT, mT, 0);
    const to = lastTo > today ? today : lastTo;
    return { start_date: iso(from), end_date: iso(to) };
  }
  if (preset === "dates") {
    if (!customFrom || !customTo) return null;
    return { start_date: customFrom, end_date: customTo };
  }
  return null;
}

const fmtNum = (n) => Number(n || 0).toLocaleString("es-AR", { maximumFractionDigits: 0 });
const fmtCur = (n) => `$${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtPct = (n) => `${Number(n || 0).toFixed(2)}%`;
const fmtPos = (n) => (n == null ? "—" : Number(n).toFixed(1));

/* ──────────────────────────── main component ──────────────────────────── */

export default function SearchTermsDualSection({ customerId, gscSiteUrl }) {
  const monthOptions = useMemo(() => buildMonthOptions(24), []);
  const defaultMonth = monthOptions[0]?.value || "";
  const [preset, setPreset] = useState("month");
  const [monthSingle, setMonthSingle] = useState(defaultMonth);
  const [monthFrom, setMonthFrom] = useState(defaultMonth);
  const [monthTo, setMonthTo] = useState(defaultMonth);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const range = useMemo(
    () => periodToDates(preset, monthSingle, monthFrom, monthTo, customFrom, customTo),
    [preset, monthSingle, monthFrom, monthTo, customFrom, customTo]
  );

  const monthOptionsAsc = useMemo(() => [...monthOptions].reverse(), [monthOptions]);

  return (
    <div className="space-y-4" data-testid="search-terms-dual">
      <section className="bg-white border border-slate-200 rounded-md p-4 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[260px]">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Período</div>
            <div className="flex flex-wrap gap-1">
              {PERIOD_PRESETS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPreset(p.id)}
                  data-testid={`terms-preset-${p.id}`}
                  className={`text-[11px] px-2.5 py-1 rounded-sm font-medium border transition-colors ${
                    preset === p.id ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {range && (
            <div className="text-[10px] font-mono text-slate-500 self-end pb-1">
              {range.start_date} → {range.end_date}
            </div>
          )}
        </div>

        {preset === "month" && (
          <div className="flex items-end gap-3 pt-2 border-t border-slate-100" data-testid="terms-month-row">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Mes</div>
              <select value={monthSingle} onChange={(e) => setMonthSingle(e.target.value)} className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white min-w-[140px]" data-testid="terms-month-single">
                {monthOptionsAsc.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="flex gap-1 mb-px">
              <button onClick={() => setMonthSingle(monthOptions[0].value)} className="text-[10px] px-2 py-1 rounded-sm border border-slate-200 hover:border-slate-400 text-slate-600">Mes actual</button>
              <button onClick={() => monthOptions[1] && setMonthSingle(monthOptions[1].value)} className="text-[10px] px-2 py-1 rounded-sm border border-slate-200 hover:border-slate-400 text-slate-600">Mes anterior</button>
            </div>
          </div>
        )}

        {preset === "months" && (
          <div className="flex items-end gap-3 pt-2 border-t border-slate-100 flex-wrap" data-testid="terms-months-row">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Desde mes</div>
              <select value={monthFrom} onChange={(e) => setMonthFrom(e.target.value)} className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white min-w-[140px]" data-testid="terms-month-from">
                {monthOptionsAsc.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Hasta mes</div>
              <select value={monthTo} onChange={(e) => setMonthTo(e.target.value)} className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white min-w-[140px]" data-testid="terms-month-to">
                {monthOptionsAsc.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="flex gap-1 mb-px">
              <button onClick={() => { setMonthFrom(monthOptions[2]?.value || monthOptions[0].value); setMonthTo(monthOptions[0].value); }} className="text-[10px] px-2 py-1 rounded-sm border border-slate-200 hover:border-slate-400 text-slate-600">Últimos 3 meses</button>
              <button onClick={() => { setMonthFrom(monthOptions[5]?.value || monthOptions[0].value); setMonthTo(monthOptions[0].value); }} className="text-[10px] px-2 py-1 rounded-sm border border-slate-200 hover:border-slate-400 text-slate-600">Últimos 6 meses</button>
              <button onClick={() => { setMonthFrom(monthOptions[11]?.value || monthOptions[0].value); setMonthTo(monthOptions[0].value); }} className="text-[10px] px-2 py-1 rounded-sm border border-slate-200 hover:border-slate-400 text-slate-600">Últimos 12 meses</button>
            </div>
          </div>
        )}

        {preset === "dates" && (
          <div className="flex items-end gap-3 pt-2 border-t border-slate-100" data-testid="terms-dates-row">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Desde</div>
              <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-8 text-xs" data-testid="terms-custom-from" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Hasta</div>
              <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-8 text-xs" data-testid="terms-custom-to" />
            </div>
          </div>
        )}
      </section>

      {!range ? (
        <div className="text-xs text-slate-400 italic px-2">Elegí un período válido para ver datos.</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <PaidTermsCard customerId={customerId} range={range} />
          <OrganicTermsCard gscSiteUrl={gscSiteUrl} range={range} />
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── Paid (Google Ads) ─────────────────────────── */

const PAID_COLS = [
  { k: "term",         label: "Término",  align: "left",  className: "px-3 py-2 font-medium text-slate-800 max-w-[180px] truncate" },
  { k: "campaign",     label: "Campaña",  align: "left",  className: "px-2 py-2 text-slate-500 text-[10px] max-w-[160px] truncate" },
  { k: "impressions",  label: "Impr.",    align: "right", numeric: true,  format: fmtNum,            className: "text-slate-700" },
  { k: "clicks",       label: "Clicks",   align: "right", numeric: true,  format: fmtNum,            className: "text-sky-700 font-semibold" },
  { k: "conversions",  label: "Conv.",    align: "right", numeric: true,  format: fmtNum,            className: "text-emerald-700 font-semibold" },
  { k: "ctr",          label: "CTR",      align: "right", numeric: true,  format: fmtPct,            className: "text-slate-600" },
  { k: "avg_cpc",      label: "CPC",      align: "right", numeric: true,  format: fmtCur,            className: "text-slate-600" },
  { k: "cost",         label: "Gasto",    align: "right", numeric: true,  format: fmtCur,            className: "text-amber-700" },
];

function PaidTermsCard({ customerId, range }) {
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState("clicks");
  const [sortDir, setSortDir] = useState("desc");

  const q = useQuery({
    queryKey: ["terms-paid", customerId, range],
    queryFn: () => fetchGoogleAdsSearchTermsDirect({ customer_id: customerId, ...range, limit: 500 }),
    enabled: !!customerId,
    staleTime: 5 * 60_000,
  });

  const rows = (q.data?.rows || []);
  const filtered = useMemo(() => {
    const cmp = (a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      const r = typeof av === "string" ? String(av).localeCompare(String(bv)) : (Number(av || 0) - Number(bv || 0));
      return sortDir === "asc" ? r : -r;
    };
    return rows
      .filter((r) => !filter.trim() || (r.term || "").toLowerCase().includes(filter.toLowerCase()))
      .sort(cmp);
  }, [rows, filter, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir(k === "term" || k === "campaign" ? "asc" : "desc"); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-md overflow-hidden flex flex-col" data-testid="paid-terms-card">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
        <div className="bg-amber-100 text-amber-700 rounded p-1.5"><DollarSign className="h-3.5 w-3.5" /></div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">Paid · Google Ads</h3>
          <p className="text-[10px] text-slate-500">{rows.length} términos · mostrando {filtered.length}</p>
        </div>
        <div className="relative w-full sm:w-52">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input placeholder="Filtrar término…" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 pl-9 bg-white text-xs" data-testid="paid-terms-filter" />
        </div>
      </div>

      {!customerId ? (
        <EmptyState icon={Target} message="Sin Google Ads Customer ID configurado." hint="Guardalo en Configurar compromiso → Google Ads Customer ID." />
      ) : q.isLoading ? (
        <div className="h-40 m-3 rounded-md skeleton" />
      ) : q.error ? (
        <ErrorState message={q.error?.response?.data?.detail || q.error?.message} />
      ) : rows.length === 0 ? (
        <EmptyMessage>Sin términos en el período seleccionado.</EmptyMessage>
      ) : (
        <DualHScroll>
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                {PAID_COLS.map((c) => (
                  <SortTh key={c.k} label={c.label} k={c.k} curr={sortKey} dir={sortDir} onClick={toggleSort} align={c.align} prefix="paid" />
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r, i) => (
                <tr key={`${r.term}-${i}`} className={`hover:bg-slate-50 ${r.conversions === 0 && r.clicks > 30 ? "bg-rose-50/30" : ""}`}>
                  {PAID_COLS.map((c) => {
                    const v = c.format ? c.format(r[c.k]) : (r[c.k] || "—");
                    return (
                      <td
                        key={c.k}
                        title={(c.k === "term" || c.k === "campaign") ? String(r[c.k] || "") : undefined}
                        className={`${c.align === "right" ? "text-right font-mono tabular-nums" : ""} ${c.className} ${c.k === "term" || c.k === "cost" ? "px-3 py-2" : "px-2 py-2"}`}
                      >
                        {v}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </DualHScroll>
      )}
      {filtered.length > 0 && <p className="text-[10px] text-slate-400 italic px-5 py-2 border-t border-slate-100">Filas en rosa: muchos clicks, 0 conversiones — candidatos a keyword negativa.</p>}
    </section>
  );
}

/* ─────────────────────────── Organic (Search Console) ─────────────────────────── */

const ORGANIC_COLS = [
  { k: "query",       label: "Query",       align: "left",  className: "px-3 py-2 font-medium text-slate-800 max-w-[220px] truncate" },
  { k: "clicks",      label: "Clicks",      align: "right", format: fmtNum, className: "text-sky-700 font-semibold" },
  { k: "impressions", label: "Impresiones", align: "right", format: fmtNum, className: "text-slate-700" },
  { k: "ctr",         label: "CTR",         align: "right", format: (n) => `${(Number(n||0)*100).toFixed(2)}%`, className: "text-slate-600" },
  { k: "position",    label: "Posición",    align: "right", format: fmtPos, className: "text-indigo-700 font-semibold" },
];

function OrganicTermsCard({ gscSiteUrl, range }) {
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState("clicks");
  const [sortDir, setSortDir] = useState("desc");

  const q = useQuery({
    queryKey: ["terms-organic", gscSiteUrl, range],
    queryFn: () => fetchSearchConsoleQuery({ site_url: gscSiteUrl, ...range, dimensions: ["query"], row_limit: 5000 }),
    enabled: !!gscSiteUrl,
    staleTime: 5 * 60_000,
  });

  const rows = useMemo(() => (q.data?.rows || []).map((r) => ({
    query: r.keys?.[0] || "",
    clicks: r.clicks || 0,
    impressions: r.impressions || 0,
    ctr: r.ctr || 0,
    position: r.position || 0,
  })), [q.data]);

  const filtered = useMemo(() => {
    const cmp = (a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      const r = sortKey === "query" ? String(av).localeCompare(String(bv)) : (Number(av || 0) - Number(bv || 0));
      return sortDir === "asc" ? r : -r;
    };
    return rows
      .filter((r) => !filter.trim() || r.query.toLowerCase().includes(filter.toLowerCase()))
      .sort(cmp);
  }, [rows, filter, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir(k === "position" || k === "query" ? "asc" : "desc"); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-md overflow-hidden flex flex-col" data-testid="organic-terms-card">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
        <div className="bg-indigo-100 text-indigo-700 rounded p-1.5"><Globe2 className="h-3.5 w-3.5" /></div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">Orgánico · Search Console</h3>
          <p className="text-[10px] text-slate-500">{rows.length} queries · mostrando {filtered.length}</p>
        </div>
        <div className="relative w-full sm:w-52">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input placeholder="Filtrar query…" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 pl-9 bg-white text-xs" data-testid="organic-terms-filter" />
        </div>
      </div>

      {!gscSiteUrl ? (
        <EmptyState icon={Globe2} message="Sin Search Console configurado." hint="Cargalo en CRO → Search Console (se guarda automáticamente)." />
      ) : q.isLoading ? (
        <div className="h-40 m-3 rounded-md skeleton" />
      ) : q.error ? (
        <ErrorState message={q.error?.response?.data?.detail || q.error?.message} />
      ) : rows.length === 0 ? (
        <EmptyMessage>Sin queries en el período seleccionado.</EmptyMessage>
      ) : (
        <DualHScroll>
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                {ORGANIC_COLS.map((c) => (
                  <SortTh key={c.k} label={c.label} k={c.k} curr={sortKey} dir={sortDir} onClick={toggleSort} align={c.align} prefix="organic" />
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <tr key={r.query} className="hover:bg-slate-50">
                  {ORGANIC_COLS.map((c) => (
                    <td
                      key={c.k}
                      title={c.k === "query" ? r.query : undefined}
                      className={`${c.align === "right" ? "text-right font-mono tabular-nums px-2 py-2" : "px-3 py-2"} ${c.className}`}
                    >
                      {c.format ? c.format(r[c.k]) : r[c.k]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </DualHScroll>
      )}
    </section>
  );
}

/* ─────────────────────────── helpers ─────────────────────────── */

/**
 * Wraps a horizontally-scrollable region with a synced scrollbar BOTH at the top
 * and at the bottom. The user asked for this so they don't have to scroll all
 * the way down to access the horizontal scrollbar.
 */
function DualHScroll({ children, maxHeight = "28rem" }) {
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const innerRef = useRef(null);
  const [innerWidth, setInnerWidth] = useState(0);
  const syncing = useRef(false);

  useEffect(() => {
    if (!innerRef.current) return;
    const measure = () => setInnerWidth(innerRef.current?.scrollWidth || 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, []);

  const onTopScroll = useCallback(() => {
    if (syncing.current) return;
    syncing.current = true;
    if (bottomRef.current && topRef.current) bottomRef.current.scrollLeft = topRef.current.scrollLeft;
    requestAnimationFrame(() => { syncing.current = false; });
  }, []);
  const onBottomScroll = useCallback(() => {
    if (syncing.current) return;
    syncing.current = true;
    if (bottomRef.current && topRef.current) topRef.current.scrollLeft = bottomRef.current.scrollLeft;
    requestAnimationFrame(() => { syncing.current = false; });
  }, []);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div
        ref={topRef}
        onScroll={onTopScroll}
        className="dual-scroll-top overflow-x-scroll overflow-y-hidden border-b border-slate-100 bg-slate-50/40"
        style={{ height: 14 }}
        data-testid="dual-scroll-top"
      >
        <div style={{ width: innerWidth, height: 1 }} />
      </div>
      <div
        ref={bottomRef}
        onScroll={onBottomScroll}
        className="overflow-x-auto overflow-y-auto flex-1"
        style={{ maxHeight }}
      >
        <div ref={innerRef} style={{ minWidth: "max-content" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function SortTh({ label, k, curr, dir, onClick, align, prefix }) {
  const active = curr === k;
  const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th className={`px-2 py-2 ${align === "right" ? "text-right" : ""}`}>
      <button
        onClick={() => onClick(k)}
        className={`inline-flex items-center gap-1 font-semibold uppercase tracking-wider text-[10px] hover:text-slate-700 ${active ? "text-indigo-700" : "text-slate-500"}`}
        data-testid={`${prefix}-sort-${k}`}
      >
        {label}
        <Icon className="h-2.5 w-2.5" />
      </button>
    </th>
  );
}

function EmptyState({ icon: Icon, message, hint }) {
  return (
    <div className="py-10 px-6 text-center bg-slate-50/60 flex-1">
      <Icon className="h-7 w-7 text-slate-300 mx-auto mb-2" />
      <div className="text-sm text-slate-500">{message}</div>
      {hint && <div className="text-[11px] text-slate-400 mt-1.5">{hint}</div>}
    </div>
  );
}

function EmptyMessage({ children }) {
  return <div className="py-10 px-6 text-center text-sm text-slate-400 flex-1">{children}</div>;
}

function ErrorState({ message }) {
  return (
    <div className="px-5 py-4 bg-rose-50 border-y border-rose-100 text-xs text-rose-800 flex items-start gap-2">
      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold">No se pudo cargar</div>
        <div className="mt-0.5">{message}</div>
      </div>
    </div>
  );
}
